/**
 * Form drafts that survive leaving the page (NFR-2).
 *
 * The daily log is filled in while walking around a shed. A partner gets
 * interrupted, takes a call, the browser drops the tab to reclaim memory —
 * which cheap Android phones do aggressively — and everything typed is gone.
 * After that happens twice, the form gets filled in from memory at the end of
 * the day, which is worse information than a partial record made at the time.
 *
 * So drafts are written to localStorage as they change and restored on mount.
 * They are explicitly *not* the source of truth: the moment the server accepts
 * a submission the draft is cleared, because a stale draft reappearing over
 * saved data is worse than losing the draft.
 */

const PREFIX = 'agrofarm.draft.';

/** Namespaced per form and per day, so yesterday's draft never resurfaces. */
export const draftKey = (form: string, scope: string): string => `${PREFIX}${form}.${scope}`;

/**
 * Reads a draft, returning null for anything that is not a usable object.
 *
 * Never throws. localStorage is unavailable in private mode on some browsers
 * and can hold corrupt JSON from an older version of the app; either would
 * otherwise take down the whole form on mount, which is the opposite of what
 * this exists to prevent.
 */
export function readDraft<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;

    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as T) : null;
  } catch {
    return null;
  }
}

/** Writes a draft, silently doing nothing when storage is unavailable or full. */
export function writeDraft(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A quota error must not break the form the draft was protecting.
  }
}

export function clearDraft(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to do: a draft that cannot be cleared is a stale draft, and the
    // day-scoped key means it expires tomorrow regardless.
  }
}

/**
 * Removes drafts from days that have passed.
 *
 * Without this a partner who fills in the log for six months accumulates a
 * hundred and eighty dead keys. Called on mount, which is often enough for a
 * form used once a day.
 */
export function clearStaleDrafts(form: string, keepScope: string): void {
  try {
    const prefix = `${PREFIX}${form}.`;
    const doomed: string[] = [];

    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key !== null && key.startsWith(prefix) && key !== `${prefix}${keepScope}`) doomed.push(key);
    }

    for (const key of doomed) window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable: there is nothing stale to clear either.
  }
}
